/** Static catalog of reports shown on /reports and in sidebar search. */

export type ReportCategory =
  | 'Analytics'
  | 'Sales'
  | 'Inventory'
  | 'Pricing'
  | 'Payments'
  | 'Reconciliation'
  | 'Content'
  | 'Team';

export interface ReportDefinition {
  id: string;
  name: string;
  description: string;
  category: ReportCategory;
  path: string;
  /**
   * Team features the report can't work without. When one is off, the Reports list and search
   * leave the report out, and its page shows ReportFeatureGate's note instead of the report.
   */
  requires?: readonly ReportFeature[];
}

/** A team feature a report may need, read from the team's settings (`teamReportFeatures`). */
export type ReportFeature = 'xero_invoice_posting';

export const REPORT_FEATURES: Record<ReportFeature, { label: string; settingsPath: string }> = {
  xero_invoice_posting: { label: 'Xero invoice posting', settingsPath: '/integrations?tab=xero' },
};

export const REPORTS: ReportDefinition[] = [
  {
    id: 'sales-dashboard',
    name: 'Sales Dashboard',
    description:
      'Full analytics home: filters, revenue trends, brand summary, and channel detail table.',
    category: 'Analytics',
    path: '/reports/sales-dashboard',
  },
  {
    id: 'forecast',
    name: 'Forecast',
    description:
      'Project monthly revenue forward from completed history, with optional channel and brand filters.',
    category: 'Analytics',
    path: '/reports/forecast',
  },
  {
    id: 'price-changes',
    name: 'Price Changes',
    description: 'See all variants whose Shopify retail price changed since a selected date.',
    category: 'Pricing',
    path: '/reports/price-changes',
  },
  {
    id: 'supplier-changes',
    name: 'Supplier Changes',
    description:
      'Supplier feed price rises, drops and items back in stock since a date, with our retail price and margin then and today.',
    category: 'Pricing',
    path: '/reports/supplier-changes',
  },
  {
    id: 'out-of-stock',
    name: 'Out of Stock',
    description:
      'Brand-level sellability for tracked products. Today uses current Shopify status; lookback is slot-weighted over each day’s stored status so archived SKUs count only when they were live.',
    category: 'Inventory',
    path: '/reports/out-of-stock',
  },
  {
    id: 'sku-stock-performance',
    name: 'SKU Stock Performance',
    description:
      'Sellability, stock value, sales, turn and ROI by variant or product from daily stock history. Sellability uses each day’s Shopify status so archived SKUs count only when they were live.',
    category: 'Inventory',
    path: '/reports/sku-stock-performance',
  },
  {
    id: 'overstock',
    name: 'Overstock',
    description:
      'SKUs with extra stock and too many months of cover. Discount them on Shopify with compare-at pricing.',
    category: 'Inventory',
    path: '/reports/overstock',
  },
  {
    id: 'markdowns',
    name: 'Markdowns',
    description:
      'Linked SKUs currently cheaper than compare-at, plus open jflo markdowns that need attention. Adopt Shopify-only sales or clear them back to list price.',
    category: 'Inventory',
    path: '/reports/markdowns',
  },
  {
    id: 'unit-sales',
    name: 'Unit Sales',
    description: 'Net units and revenue by SKU for a date range. Filter by brand(s) or brand tags.',
    category: 'Sales',
    path: '/reports/unit-sales',
  },
  {
    id: 'shopify-sales',
    name: 'Shopify Sales',
    description:
      'Order lines with SKU, subtotals, per-line taxes, shipping, billing and shipping countries. Filter by date range and export to CSV.',
    category: 'Sales',
    path: '/reports/shopify-sales',
  },
  {
    id: 'shopify-payments',
    name: 'Shopify Payments',
    description:
      'Order payment transactions with paid date, payment method, reference, amount, and type. Filter by paid date range and export to CSV.',
    category: 'Payments',
    path: '/reports/shopify-payments',
  },
  {
    id: 'order-reconciliation',
    name: 'Order Reconciliation',
    description:
      'Compare Shopify orders and Xero invoices for a date range. Spot missing sides, total mismatches, and expand rows for line items and payments.',
    category: 'Reconciliation',
    path: '/reports/order-reconciliation',
    // Pairs orders with the invoices jflo posts to Xero (by their invoice number prefix).
    requires: ['xero_invoice_posting'],
  },
  {
    id: 'xero-stock-not-on-shopify',
    name: 'Xero Stock Not on Shopify',
    description: 'Variants that Xero reports stock for, but which have no linked Shopify product.',
    category: 'Inventory',
    path: '/reports/xero-stock-not-on-shopify',
  },
  {
    id: 'orphan-variant-revenue',
    name: 'Orphan Variants with Revenue',
    description:
      'Variants with sales history but no linked product. Link them to a product, and schedule email alerts when any appear.',
    category: 'Reconciliation',
    path: '/reports/orphan-variant-revenue',
  },
  {
    id: 'unseen-stock',
    name: 'Unseen Stock',
    description:
      'Products in stock that get little traffic, by product, brand or collection, with why each one is unseen and what to do about it.',
    category: 'Inventory',
    path: '/reports/unseen-stock',
  },
  {
    id: 'collection-problems',
    name: 'Collection Problems',
    description:
      'Collections missing a description or SEO, with an invalid handle, a Brand mismatch, or no products. Schedule it to make a task.',
    category: 'Content',
    path: '/reports/collection-problems',
  },
  {
    id: 'swatch-problems',
    name: 'Swatch Problems',
    description:
      'Colour names with no swatch, spelt more than one way, or not on any product. Schedule it to make a task.',
    category: 'Content',
    path: '/reports/swatch-problems',
  },
  {
    id: 'wasted-ad-spend',
    name: 'Wasted Ad Spend',
    description:
      'Google and Microsoft ad spend with no conversions: search terms, your own name, other shops, products and pages, each with the negative keyword or exclusion to add.',
    category: 'Analytics',
    path: '/reports/wasted-ad-spend',
  },
  {
    id: 'capacity-planner',
    name: 'Capacity Planner',
    description:
      'Store sales, online orders, customer emails and receiving by weekday and hour, and the people the store and warehouse need, with minutes per task you set.',
    category: 'Analytics',
    path: '/reports/capacity-planner',
  },
  {
    id: 'inbox-workload',
    name: 'Inbox Workload',
    description:
      'Customer emails in and replies out by day, weekday and hour, how long customers wait for a reply, and replies by person.',
    category: 'Analytics',
    path: '/reports/inbox-workload',
  },
  {
    id: 'meta-ads-check',
    name: 'Meta Ads Check',
    description:
      'Whether any Facebook or Instagram ad is running. Schedule it to make a task when none are, or when the Meta data is out of date.',
    category: 'Analytics',
    path: '/reports/meta-ads-check',
  },
  {
    id: 'google-ads-check',
    name: 'Ads Check',
    description:
      'Google, Microsoft and Meta Ads that need attention: low returns, no conversions or spend, disapproved ads, over budget, refused products, PMax groups and assets to fix, and wasted spend. Schedule it to make a task.',
    category: 'Analytics',
    path: '/reports/google-ads-check',
  },
  {
    id: 'time-spent',
    name: 'Time Spent',
    description:
      'Where the team spends its time in jflo and the Chrome extension: by area and person, and per receiving session, order, conversation or product, with the average.',
    category: 'Team',
    path: '/reports/time-spent',
  },
];

export const REPORT_CATEGORIES: ReportCategory[] = [
  'Analytics',
  'Sales',
  'Inventory',
  'Pricing',
  'Payments',
  'Reconciliation',
  'Content',
  'Team',
];

export function reportMatchesQuery(report: ReportDefinition, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  return (
    report.name.toLowerCase().includes(q) ||
    report.description.toLowerCase().includes(q) ||
    report.category.toLowerCase().includes(q)
  );
}

/** Which report features a team has on, from its settings (`active_team.settings`). */
export function teamReportFeatures(settings: unknown): ReadonlySet<ReportFeature> {
  const on = new Set<ReportFeature>();
  if (!settings || typeof settings !== 'object') return on;
  const posting = (settings as Record<string, unknown>).xeroInvoicePosting;
  if (posting && typeof posting === 'object' && (posting as { enabled?: unknown }).enabled === true) {
    on.add('xero_invoice_posting');
  }
  return on;
}

/** The features `report` needs that the team has off (empty when it can run). */
export function missingReportFeatures(
  report: ReportDefinition,
  features: ReadonlySet<ReportFeature>
): ReportFeature[] {
  return (report.requires ?? []).filter((feature) => !features.has(feature));
}

/** Whether the team has every feature `report` needs. */
export function reportIsAvailable(
  report: ReportDefinition,
  features: ReadonlySet<ReportFeature>
): boolean {
  return missingReportFeatures(report, features).length === 0;
}

/** The catalog's reports the team can run. */
export function availableReports(features: ReadonlySet<ReportFeature>): ReportDefinition[] {
  return REPORTS.filter((report) => reportIsAvailable(report, features));
}

export function reportById(id: string): ReportDefinition | undefined {
  return REPORTS.find((report) => report.id === id);
}
